import * as React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {HomePage} from '../components';
import {useProfilePackageStore} from "../../../redux/profile-packages";
import {useUpcomingEventsStore} from "../../../redux/upcoming-events";
import {useCustomerStore} from "../../../redux/customers";
import {useBookingsStore} from "../../../redux/bookings";
import {useToastStore} from "../../../redux/toast";
import {useHolidaysStore} from "../../../redux/holidays";
import {upcomingHolidays} from "../../../utils/holidays";
import {useHistory} from "react-router";
import {useAuthStore} from "../../../redux/auth";
import {Booking, Customer} from "../../../models";

type Props = {
};

export const HomePageContainer: React.FC<Props> = props => {
    const refresherRef = useRef<HTMLIonRefresherElement>(null);
    const history = useHistory();
    const toastStore = useToastStore();
    const authStore = useAuthStore();
    const customerStore = useCustomerStore();
    const bookingsStore = useBookingsStore();
    const profilePackageStore = useProfilePackageStore();
    const upcomingEventsStore = useUpcomingEventsStore();
    const {holidaysByActiveCustomer} = useHolidaysStore();
    const upcomingHolidayRows = useMemo(
        () => upcomingHolidays(holidaysByActiveCustomer ?? [], new Date()),
        [holidaysByActiveCustomer]);
    const {authStatus} = authStore;
    const [selectedBooking, setSelectedBooking] = useState<Booking>();

    const {
        errorMessage: profilePackageErrorMessage,
        clearErrors: profilePackageClearErrors,
    } = profilePackageStore;

    const {
        errorMessage: bookingsErrorMessage,
        clearErrors: bookingsClearErrors,
    } = bookingsStore;

    const {
        upcomingEvents,
        errorMessage: upcomingEventsErrorMessage,
        clearErrors: upcomingEventsClearErrors,
    } = upcomingEventsStore;

    const {
        isFetching: customerIsFetching,
        selectedId: selectedCustomerId,
        errorMessage: customerErrorMessage,
        clearErrors: customerClearErrors,
        activeCustomer,
        customersCount,
        getById,
        getAll,
    } = customerStore;

    const canEditProfile = !!(!customerIsFetching && activeCustomer?.company?.featuresSelfManage);

    const completeRefresh = useCallback(() => {
        if (refresherRef.current) {
            refresherRef.current.complete();
        }
    }, [refresherRef]);

    const fetchRelatedData = useCallback((customerId: number) => {
        Promise.all([
            bookingsStore.getAll({customerId}),
            profilePackageStore.getAll({customerId}),
            upcomingEventsStore.getAll({customerId}),
        ])
            .catch(() => { /** Handled by state and toast */ })
            .finally(completeRefresh)
    }, [activeCustomer, bookingsStore, toastStore, profilePackageStore, upcomingEventsStore])

    const fetchCustomerData = useCallback(async () => {
        if (!authStatus || !selectedCustomerId || typeof selectedCustomerId !== "number") {
            return;
        }

        try {
            if (customersCount && customersCount > 1) {
                await getById(selectedCustomerId);
            } else {
                // Single-customer path: getAll() succeeds even when the persisted
                // selectedId was revoked/recreated server-side. Reconcile it here —
                // mirror the multi-customer 404 redirect — so a dead id lands on the
                // selector instead of a blank home with three fetches on a stale id.
                const result = await getAll();
                const fetchedCustomers = Object.values(result?.results?.customers ?? {}) as Customer[];
                const stillExists = fetchedCustomers.some(customer => customer.id === selectedCustomerId);
                if (!stillExists) {
                    history.replace('/customer-select');
                    return;
                }
            }

            fetchRelatedData(selectedCustomerId);
        } catch (error) {
            completeRefresh();
            if (typeof error === 'object' && error !== null) {
                const apiError = error as { status?: number };
                if (apiError.status === 404) {
                    history.replace('/customer-select');
                }
            }
        }
    }, [getById, getAll, selectedCustomerId, customersCount]);

    const prevSelectedCustomerIdRef = useRef(selectedCustomerId);

    useEffect(() => {
        fetchCustomerData();
        completeRefresh();
    }, [])

    useEffect(() => {
        // Refetch on customer SWITCH only; on mount fetchCustomerData() owns the
        // related-data fetch (after validating the customer), so an unchanged id
        // means this is the initial run — skip it to avoid a duplicate triple.
        if (selectedCustomerId === prevSelectedCustomerIdRef.current) {
            return;
        }
        prevSelectedCustomerIdRef.current = selectedCustomerId;
        if (authStatus && selectedCustomerId && typeof selectedCustomerId === "number"){
            fetchRelatedData(selectedCustomerId);
        }
    }, [selectedCustomerId])

    useEffect(() => {
        bookingsErrorMessage && toastStore.showError({message: bookingsErrorMessage}).then(() => {
            bookingsClearErrors();
        });
    }, [bookingsErrorMessage])

    useEffect(() => {
        profilePackageErrorMessage && toastStore.showError({message: profilePackageErrorMessage}).then(() => {
            profilePackageClearErrors();
        });
    }, [profilePackageErrorMessage])

    useEffect(() => {
        customerErrorMessage && toastStore.showError({message: customerErrorMessage}).then(() => {
            customerClearErrors();
        });
    }, [customerErrorMessage])

    useEffect(() => {
        upcomingEventsErrorMessage && toastStore.showError({message: upcomingEventsErrorMessage}).then(() => {
            upcomingEventsClearErrors();
        });
    }, [upcomingEventsErrorMessage])

    const handleRefresh = async (event: CustomEvent) => {
        if (!customerIsFetching){
            fetchCustomerData();
        }
    }

    const handleEditProfile = async (profileId: number, event: React.MouseEvent<HTMLIonCardTitleElement, MouseEvent>) => {
        if (!customerIsFetching){
            history.push(`/profile/${profileId}`);
        }
    }

    return <HomePage
        customer={activeCustomer}
        doRefresh={handleRefresh}
        refresherRef={refresherRef}

        canEditProfile={canEditProfile}
        selectedBooking={selectedBooking}
        setSelectedBooking={setSelectedBooking}

        upcomingEvents={upcomingEvents}
        upcomingHolidays={upcomingHolidayRows}

        onEditProfileClicked={handleEditProfile}
    />;
};
